import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Box,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  Card,
  CardContent,
} from '@mui/material';

import { TP_COLORS } from '../../../../constants';
import { getSubjectColor } from '../../../../helper';
import {
  adminUpdateTopicSorting,
  IAdminUpdateTopicSorting,
} from '../../../../store/TopicSlice';

interface ISelectedOption {
  label: string;
  value: string;
  sortingOrder?: number;
}

interface EditTopicModalProps {
  open: boolean;
  onClose: () => void;
  topics: ISelectedOption[];
  subjectLevel: string;
  subject: string;
  curriculum: string;
  grade: string;
  subjectCode: string;
}

const EditTopicModal: React.FC<EditTopicModalProps> = ({
  open,
  onClose,
  topics,
  subjectLevel,
  subject,
  curriculum,
  grade,
  subjectCode,
}) => {
  const dispatch = useDispatch();

  const [newTopics, setNewTopics] = useState<Array<ISelectedOption>>([]);

  useEffect(() => {
    setNewTopics(topics);
  }, []);

  const handleSortingNumberChange = (
    topic: ISelectedOption,
    sortNumber: string
  ) => {
    setNewTopics((prevTopics) => {
      const newTopicList = [...prevTopics];
      const index = newTopicList.findIndex((newTopic) => newTopic === topic);

      if (index > -1 && newTopicList[index]) {
        newTopicList[index] = {
          ...topic,
          sortingOrder: sortNumber
            ? Math.abs(parseFloat(sortNumber))
            : undefined,
        };
      }

      return newTopicList;
    });
  };

  const handleSubmit = () => {
    const apiData: IAdminUpdateTopicSorting = {
      topics: [],
    };

    if (newTopics && newTopics.length) {
      newTopics.forEach((option) => {
        apiData.topics.push({
          topicId: option.value,
          sortingOrder: option.sortingOrder,
        });
      });
    }

    dispatch(adminUpdateTopicSorting(apiData));

    onClose();
  };

  return (
    <Dialog open={open} PaperProps={{ style: { minWidth: '600px' } }}>
      <DialogContent>
        <Box role="presentation">
          <Card
            style={{
              margin: '0 -16px 15px -16px',
              background: `url('${
                process.env.PUBLIC_URL
              }/images/shapes/learning-gadgets.transparent.png') top right/contain no-repeat, ${getSubjectColor(
                subject
              )}`,
            }}
          >
            <CardContent style={{ marginBottom: '16px' }}>
              <Typography
                variant="h6"
                className="ellipsis-text"
                sx={{ color: TP_COLORS.white }}
              >
                {subject} - {subjectCode}
              </Typography>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                className="ellipsis-text"
                sx={{ color: TP_COLORS.white, opacity: 0.5 }}
              >
                {curriculum} - {grade}
              </Typography>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                className="ellipsis-text"
                sx={{ color: TP_COLORS.white, opacity: 0.5 }}
              >
                {subjectLevel}
              </Typography>
            </CardContent>
          </Card>
          <div style={{ marginBottom: '15px' }}>
            <Typography variant="h6" fontWeight={600}>
              Topics
            </Typography>
          </div>
          <div>
            {newTopics.length > 0
              ? newTopics.map((newTopic, index) => {
                  const sortingNumberValue =
                    newTopics[index] && newTopics[index].sortingOrder
                      ? newTopics[index].sortingOrder?.toString()
                      : '';

                  return (
                    <Grid
                      key={index}
                      container
                      alignItems="center"
                      style={{ marginBottom: '5px' }}
                    >
                      <Grid item xs={9}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {newTopic.label}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          type="number"
                          size="small"
                          value={sortingNumberValue}
                          placeholder="Order No"
                          onChange={(event) =>
                            handleSortingNumberChange(
                              newTopic,
                              event.target.value
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                  );
                })
              : null}
          </div>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '16px' }}>
        <Button variant="contained" color="inherit" onClick={onClose}>
          Dismiss
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTopicModal;
